import { reaction, runInAction, when } from "mobx";
import { initComponentStore } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { v4 as uuidv4 } from "uuid";
import { DeploymentServiceStore } from "@/app_store/agent/DeploymentService.generated";
import { BrowserSessionStore } from "@/app_store/agent/BrowserSession.generated";
import { TerminalSessionStore } from "@/app_store/agent/TerminalSession.generated";
import { TOUCH_MEDIA_QUERY, isTouchDevice } from "@/utils/device";

export type { DeploymentServiceStore as DeploymentService };
export type { BrowserSessionStore as BrowserSession };
export type { TerminalSessionStore as TerminalSession };

export interface LogTab {
    id: string;
    label: string;
    type: "deploy" | "logs";
}

export type SubTab = "services" | "browser" | "terminal";

const LANDSCAPE_QUERY = "(min-aspect-ratio: 1)";

export class DeploymentSectionStore {
    activeEnv: string = "";
    customService: string = "";
    proxyTarget: string = "http://localhost:8085";
    isMobile: boolean = isTouchDevice();
    isLandscape: boolean = window.matchMedia(LANDSCAPE_QUERY).matches;
    activeBrowserTabId: string | null = null;
    initializingBrowserTabIds: string[] = [];
    activeTerminalTabId: string | null = null;
    newTerminalSessionIds: Set<string> = new Set();
    searchQuery: string = "";
    logTabs: LogTab[] = [];
    activeLogTabId: string | null = null;
    wordWrap: boolean = false;
    logActionCount: number = 0;
    _localLogFontSize: number | null = null;
    activeSubTab: SubTab = "services";
    // Not observable — holds a MobX when() disposer
    _reconnectDisposer: (() => void) | null = null;

    constructor() {
        initComponentStore(this, { _reconnectDisposer: false });
        window.matchMedia(TOUCH_MEDIA_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isMobile = e.matches; });
        });
        window.matchMedia(LANDSCAPE_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isLandscape = e.matches; });
        });
        // On fresh open (no tabs yet): show tabs for processes already running on the backend.
        // On reconnect with existing tabs: nothing to do — backend retains process state.
        reaction(
            () => this.deploymentContext,
            (ctx) => {
                this._reconnectDisposer?.();
                this._reconnectDisposer = null;
                if (!ctx || this.logTabs.length > 0) return;
                this._reconnectDisposer = when(
                    () => ctx.processes.processes.length > 0,
                    () => {
                        runInAction(() => {
                            for (const p of ctx.processes.processes) {
                                if (!p.is_finished) {
                                    // p.name is "Deploy: svc" or "Logs: svc" — use same id scheme as deployService/showServiceLogs
                                    const deployMatch = p.name.match(/^Deploy: (.+)$/);
                                    const logsMatch = p.name.match(/^Logs: (.+)$/);
                                    if (deployMatch) this.openLogTab(`deploy-${deployMatch[1]}`, p.name, "deploy");
                                    else if (logsMatch) this.openLogTab(`logs-${logsMatch[1]}`, p.name, "logs");
                                }
                            }
                        });
                    }
                );
            },
            { fireImmediately: true }
        );
        // Restore saved env from project, falling back to the first available environment
        reaction(
            () => ({ envs: this.envs, saved: appStore.project.selectedProject?.deployment_env }),
            ({ envs, saved }) => {
                if (this.activeEnv) return;
                const restored = saved && envs.includes(saved) ? saved : envs[0];
                if (restored) this.activeEnv = restored;
            },
            { fireImmediately: true }
        );
        // Keep activeBrowserTabId pointing at a valid tab (real or initializing)
        reaction(
            () => this.allBrowserTabs.map(t => t.id),
            (ids) => {
                if (this.activeBrowserTabId && !ids.includes(this.activeBrowserTabId)) {
                    this.activeBrowserTabId = ids[ids.length - 1] ?? null;
                }
            }
        );
        // Keep activeTerminalTabId pointing at a valid session
        reaction(
            () => this.terminalSessions.map(s => s.id),
            (ids) => {
                if (this.activeTerminalTabId && !ids.includes(this.activeTerminalTabId)) {
                    this.activeTerminalTabId = ids[0] ?? null;
                }
            }
        );
    }

    get deploymentContext() {
        return appStore.agent.session.project?.deployment ?? null;
    }

    get envList() {
        return Object.values(this.deploymentContext?.envs ?? {});
    }

    get envs(): string[] {
        return this.envList.map(e => e.name);
    }

    get envErrors(): Record<string, string | null> {
        const result: Record<string, string | null> = {};
        for (const e of this.envList) result[e.name] = e.short_error ?? null;
        return result;
    }

    get services(): DeploymentServiceStore[] {
        return this.envList.find(e => e.name === this.activeEnv)?.services ?? [];
    }

    get activeEnvError(): string | null {
        return this.envList.find(e => e.name === this.activeEnv)?.short_error ?? null;
    }

    get activeEnvErrorDetail(): string | null {
        return this.envList.find(e => e.name === this.activeEnv)?.error ?? null;
    }

    get filteredServices(): DeploymentServiceStore[] {
        const q = this.searchQuery.toLowerCase().trim();
        if (!q) return this.services;
        return this.services.filter(s => s.name.toLowerCase().includes(q));
    }

    get browserSessions(): BrowserSessionStore[] {
        return this.deploymentContext?.browser_sessions ?? [];
    }

    get allBrowserTabs(): Array<{ id: string; session: BrowserSessionStore | null }> {
        const sessionIds = new Set(this.browserSessions.map(s => s.id));
        const initializing = this.initializingBrowserTabIds
            .filter(id => !sessionIds.has(id))
            .map(id => ({ id, session: null as BrowserSessionStore | null }));
        return [...this.browserSessions.map(s => ({ id: s.id, session: s })), ...initializing];
    }

    get terminalSessions(): TerminalSessionStore[] {
        return this.deploymentContext?.terminal_sessions ?? [];
    }

    get activeLogProcess() {
        const tab = this.logTabs.find(t => t.id === this.activeLogTabId);
        if (!tab) return null;
        return this.deploymentContext?.processes.processes.find(p => p.name === tab.label) ?? null;
    }

    get deployLogs(): string[] {
        return this.activeLogProcess?.output_lines ?? [];
    }

    // Absolute index of deployLogs[0] after the backend scrollback has evicted
    // older lines -- feeds Virtuoso's firstItemIndex so each line keeps a stable
    // key across eviction and auto-follow doesn't detach under heavy output.
    get deployLogBaseIndex(): number {
        return this.activeLogProcess?.output_base_index ?? 0;
    }

    get isDeploying(): boolean {
        return this.deploymentContext?.processes.processes.some(p => !p.is_finished) ?? false;
    }

    setActiveEnv(env: string) {
        this.activeEnv = env;
        appStore.project.setDeploymentEnv(env);
    }
    setSearchQuery(value: string) { this.searchQuery = value; }
    setCustomService(value: string) { this.customService = value; }
    setProxyTarget(value: string) { this.proxyTarget = value; }
    setActiveSubTab(tab: SubTab) { this.activeSubTab = tab; }

    selectLogTab(id: string) { this.activeLogTabId = id; }
    setWordWrap(v: boolean) { this.wordWrap = v; }

    openLogTab(id: string, label: string, type: "deploy" | "logs") {
        if (!this.logTabs.some(t => t.id === id)) {
            this.logTabs.push({ id, label, type });
        }
        this.activeLogTabId = id;
    }

    closeLogTab(id: string) {
        const tab = this.logTabs.find(t => t.id === id);
        // Optimistically remove tab before RPC round-trip
        const idx = this.logTabs.findIndex(t => t.id === id);
        this.logTabs = this.logTabs.filter(t => t.id !== id);
        if (this.activeLogTabId === id) {
            const next = this.logTabs[idx] ?? this.logTabs[idx - 1] ?? null;
            this.activeLogTabId = next?.id ?? null;
        }
        if (tab) {
            const process = this.deploymentContext?.processes.processes.find(p => p.name === tab.label);
            if (process) void process.stop();
        }
    }

    deployService(service: DeploymentServiceStore) {
        const id = `deploy-${service.name}`;
        this.openLogTab(id, `Deploy: ${service.name}`, "deploy");
        this.logActionCount++;
        return service.deploy();
    }

    showServiceLogs(service: DeploymentServiceStore) {
        const id = `logs-${service.name}`;
        this.openLogTab(id, `Logs: ${service.name}`, "logs");
        this.logActionCount++;
        return service.logs();
    }

    get logFontSize(): number {
        if (this._localLogFontSize !== null) return this._localLogFontSize;
        // Per-project scale wins; 0/undefined means never zoomed, so fall back to the
        // account-wide default so the user's preferred size still applies to new projects.
        const project = appStore.project.selectedProject;
        const saved = this.isMobile ? project?.deploy_output_scale_mobile : project?.deploy_output_scale;
        if (saved) return saved;
        const settings = appStore.account.settings;
        return (this.isMobile ? settings?.deploy_log_font_size_mobile : settings?.deploy_log_font_size_desktop) ?? 11;
    }

    setLogFontSize(size: number) {
        this._localLogFontSize = size;
        appStore.project.setDeployOutputScale(size, this.isMobile);
    }

    // Browser sessions

    async openBrowserSession() {
        await this.deploymentContext?.create_browser_session(this.proxyTarget);
    }

    async openInlineBrowserTab() {
        const id = uuidv4();
        runInAction(() => {
            this.initializingBrowserTabIds.push(id);
            this.activeBrowserTabId = id;
        });

        const result = await this.deploymentContext?.create_browser_session("about:newtab", id);

        runInAction(() => {
            const idx = this.initializingBrowserTabIds.indexOf(id);
            if (idx < 0) {
                // Tab was closed while RPC was in flight — destroy the session that was created
                if (result) void this.deploymentContext?.destroy_browser_session(id);
                return;
            }
            this.initializingBrowserTabIds.splice(idx, 1);
        });
    }

    setActiveBrowserTab(id: string | null) { this.activeBrowserTabId = id; }

    async removeBrowserSession(id: string) {
        const initIdx = this.initializingBrowserTabIds.indexOf(id);
        if (initIdx >= 0) {
            // Still initializing — remove ghost tab; openInlineBrowserTab will destroy on return
            this.initializingBrowserTabIds.splice(initIdx, 1);
            if (this.activeBrowserTabId === id) {
                const tabs = this.allBrowserTabs.filter(t => t.id !== id);
                this.activeBrowserTabId = tabs.length > 0 ? tabs[tabs.length - 1].id : null;
            }
            return;
        }
        await this.deploymentContext?.destroy_browser_session(id);
    }

    // Terminal sessions

    async openNewInlineTerminal(serviceName?: string | null, envName?: string | null) {
        await this.deploymentContext?.create_terminal_session(serviceName ?? null, envName ?? null);
        const sessions = this.terminalSessions;
        if (sessions.length > 0) {
            const id = sessions[sessions.length - 1].id;
            this.newTerminalSessionIds.add(id);
            this.activeTerminalTabId = id;
        }
    }

    async openServiceTerminal(service: DeploymentServiceStore) {
        await this.openNewInlineTerminal(service.name, this.activeEnv);
        this.activeSubTab = "terminal";
    }

    setActiveTerminalTab(id: string | null) { this.activeTerminalTabId = id; }

    async closeTerminalSession(id: string) {
        this.newTerminalSessionIds.delete(id);
        await this.deploymentContext?.destroy_terminal_session(id);
    }

    dispose() {
        this._reconnectDisposer?.();
    }
}
